
import React from 'react';
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, AlertCircle, Brain, Loader2, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { OpenAIBioEvaluation } from '@/utils/openAIBioEvaluator';
import { getBioCategoryFromScore } from '@/utils/bioCategories';

interface BioQualityIndicatorProps {
  bio: string;
  className?: string;
  aiEvaluation?: OpenAIBioEvaluation & { hasBeenValidated?: boolean };
}

export const BioQualityIndicator: React.FC<BioQualityIndicatorProps> = ({ 
  bio, 
  className = "",
  aiEvaluation
}) => {
  if (!bio || bio.trim().length < 5) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="p-3 bg-white/5 rounded-lg border border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="h-4 w-4 text-white/50" />
            <span className="text-sm text-white/70">
              Qualidade da Bio (Inteligência Artificial)
            </span>
          </div>
          <p className="text-xs text-white/50">
            Comece a escrever para ver a análise de qualidade...
          </p>
        </div>
      </div>
    );
  }

  // Only show quality if AI has validated successfully
  const showQuality = aiEvaluation?.hasBeenValidated;
  const score = aiEvaluation?.score || 0;
  const isValid = aiEvaluation?.isWellWritten || false;
  const category = showQuality ? getBioCategoryFromScore(score) : null;
  
  const getFeedbackMessage = () => {
    if (aiEvaluation?.loading) {
      return "🤖 Inteligência artificial analisando sua bio...";
    }
    
    if (aiEvaluation?.error) {
      return `⚠️ ${aiEvaluation.error}`;
    }
    
    if (!showQuality) {
      return "✏️ Continue escrevendo para ativar a análise de qualidade...";
    }
    
    if (!category?.showFeedback) {
      return category?.motivationalMessage || "Biografia perfeita!";
    }
    
    return aiEvaluation?.feedback || "Continue escrevendo para melhorar sua bio...";
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Main Quality Section */}
      <div className="p-3 bg-white/5 rounded-lg border border-white/10">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">Qualidade da Bio:</span>
            <div className="flex items-center gap-1">
              {aiEvaluation?.loading ? (
                <Loader2 className="h-3 w-3 text-blue-400 animate-spin" />
              ) : (
                <Brain className="h-3 w-3 text-blue-400" />
              )}
              <span className="text-xs text-blue-400">IA</span>
            </div>
          </div>
          
          {/* Only show quality indicators if validated */}
          {showQuality && category && (
            <div className="flex items-center gap-2">
              <span className="text-lg">{category.icon}</span>
              <span className={`text-sm font-bold capitalize ${category.color}`}>
                {category.level}
              </span>
            </div>
          )}
        </div>
        
        {/* Progress bar - only show if validated */}
        {showQuality && category && (
          <Progress 
            value={category.progressValue} 
            className="h-2 bg-white/10 mb-2" 
            indicatorClassName={category.bgColor}
          />
        )}
        
        {/* Feedback message */}
        <p className="text-xs text-white/70">
          {getFeedbackMessage()}
        </p>
      </div>

      {/* AI Topics - Only when validated and well written */}
      {showQuality && aiEvaluation && isValid && aiEvaluation.topicsMentioned.length > 0 && (
        <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="h-4 w-4 text-green-400" />
            <span className="text-sm font-medium text-green-400">Tópicos detectados:</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {aiEvaluation.topicsMentioned.slice(0, 12).map(topic => (
              <Badge key={topic} variant="secondary" className="text-xs bg-green-500/20 text-green-300 border-green-500/30">
                {topic}
              </Badge>
            ))}
            {aiEvaluation.topicsMentioned.length > 12 && (
              <Badge variant="secondary" className="text-xs bg-green-500/20 text-green-300 border-green-500/30">
                +{aiEvaluation.topicsMentioned.length - 12} mais
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Excellence celebration - only if validated */}
      {showQuality && category?.level === 'excelente' && (
        <div className="p-3 bg-gradient-to-r from-emerald-500/10 to-green-500/10 rounded-lg border border-emerald-500/30">
          <div className="flex items-center justify-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-400" />
            <span className="text-sm font-medium text-emerald-400">
              Parabéns! Sua bio está excelente! 🎉
            </span>
            <Sparkles className="h-4 w-4 text-emerald-400" />
          </div>
        </div>
      )}
    </div>
  );
};
